import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import { Button, Paper, Typography } from "@mui/material";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import { downloadRawFeed, getRawFeed, normalizeCatalogFeed, uploadRawFeed } from "./services/catalogMappingService";
import CatalogTablePagination from "./CatalogTablePagination";
import CatalogLoadingPlaceholder from "./CatalogLoadingPlaceholder";
import CatalogNativeSelect from "./CatalogNativeSelect";
import CatalogTabStatusMessage from "./CatalogTabStatusMessage";
import GoToLinePanel from "./GoToLinePanel";
import { highlightFeedLineWhenReady } from "./utils/scrollFeedLineIntoView";
import {
  GDRIVE_OPTIONS,
  HEADER_OPTIONS,
  SOURCE_TYPE_OPTIONS,
} from "./utils/catalogMappingTableUtils";
import { resolveRawFeedTabStatus } from "./utils/rawFeedTabStatus";
import { getFormatType, parseSourceTypeConfig, updateSourceTypeConfig } from "./utils/sourceTypeConfig";
import { CM_TOOLTIPS } from "./utils/tooltipsContent";

const headerModeValue = (config) => (config?.is_headless ? "false" : "true");

const headerModePatch = (mode) => ({ is_headless: mode !== "true" });

const gdriveModeValue = (config) => (config?.gdrive === true ? "true" : "false");

const gdriveModePatch = (mode) => ({ gdrive: mode === "true" });

const RAW_FEED_UPLOAD_ACCEPT = ".csv,.xml,.xlsx,.xls,.txt,.tsv,.json,.zip,.rss,.atom,text/csv,application/xml,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel";

const resolvePrimaryFeedUrl = (feedUrl) => {
  const raw = String(feedUrl || "").trim();
  if (!raw) {
    return "";
  }
  return raw.split(";")[0].trim();
};

const isBrowserOpenableFeedUrl = (url) => /^https?:\/\//i.test(String(url || "").trim());

const applyFeedConfigFromResponse = (response, onConfigChange) => {
  const feedConfig = response?.feed_config;
  if (!feedConfig || typeof feedConfig !== "object" || !feedConfig.type) {
    return;
  }
  onConfigChange(parseSourceTypeConfig(feedConfig));
};

const RawFeedTab = ({
  vendorId,
  feedUrl = "",
  sourceTypeConfig,
  onSourceTypeConfigChange,
  onNormalized,
  onSave,
  showError,
  runTrackedAction,
  formatActivitySeparator,
  isActive = false,
  mappingReadOnly = false,
}) => {
  const [rawFeed, setRawFeed] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [loading, setLoading] = useState(false);
  const [rawFeedFetched, setRawFeedFetched] = useState(false);
  const [downloading, setDownloading] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [normalizing, setNormalizing] = useState(false);
  const [autoMapping, setAutoMapping] = useState(false);
  const [scrollTargetLine, setScrollTargetLine] = useState(null);
  const rawBlockRef = useRef(null);
  const uploadInputRef = useRef(null);

  const feedType = getFormatType(sourceTypeConfig);

  const primaryFeedUrl = useMemo(() => resolvePrimaryFeedUrl(feedUrl), [feedUrl]);
  const canOpenFeedUrl = isBrowserOpenableFeedUrl(primaryFeedUrl);

  const totalPages = Math.max(1, Math.ceil((rawFeed?.total_lines || 0) / rowsPerPage));

  const loadRawFeed = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getRawFeed(vendorId, page * rowsPerPage, rowsPerPage);
      setRawFeed(data);
      setRawFeedFetched(true);
    } catch (error) {
      showError(error.message);
    } finally {
      setLoading(false);
    }
  }, [vendorId, page, rowsPerPage, showError]);

  useEffect(() => {
    if (!isActive) {
      return;
    }
    loadRawFeed();
  }, [isActive, loadRawFeed]);

  useEffect(() => {
    setRawFeed(null);
    setRawFeedFetched(false);
    setPage(0);
    setScrollTargetLine(null);
  }, [vendorId]);

  useEffect(() => {
    if (!scrollTargetLine || loading || !rawFeed?.is_text) {
      return undefined;
    }
    return highlightFeedLineWhenReady(rawBlockRef.current, scrollTargetLine, () => {
      setScrollTargetLine(null);
    });
  }, [scrollTargetLine, loading, rawFeed, page, rowsPerPage]);

  const tabStatus = useMemo(
    () => resolveRawFeedTabStatus(rawFeed, loading, { fetched: rawFeedFetched }),
    [rawFeed, loading, rawFeedFetched],
  );

  const handleDownload = async () => {
    setDownloading(true);
    try {
      const downloadAction = () => downloadRawFeed(vendorId);
      const response = runTrackedAction
        ? await runTrackedAction(downloadAction, {
          separator: formatActivitySeparator?.("Download feed", vendorId),
          progressLabel: "Download feed in corso",
        })
        : await downloadAction();
      applyFeedConfigFromResponse(response, onSourceTypeConfigChange);
      setPage(0);
      await loadRawFeed();
    } catch (error) {
      showError(error.message);
    } finally {
      setDownloading(false);
    }
  };

  const handleUploadClick = () => {
    if (mappingReadOnly || uploading || downloading) {
      return;
    }
    uploadInputRef.current?.click();
  };

  const handleOpenFeedUrl = () => {
    if (!canOpenFeedUrl) {
      showError("URL feed non configurato o non apribile nel browser (solo HTTP/HTTPS)");
      return;
    }
    window.open(primaryFeedUrl, "_blank", "noopener,noreferrer");
  };

  const handleUploadFileChange = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) {
      return;
    }

    setUploading(true);
    try {
      const uploadAction = () => uploadRawFeed(vendorId, file);
      const response = runTrackedAction
        ? await runTrackedAction(uploadAction, {
          separator: formatActivitySeparator?.("Upload feed", vendorId),
          progressLabel: "Caricamento feed in corso",
        })
        : await uploadAction();
      applyFeedConfigFromResponse(response, onSourceTypeConfigChange);
      setPage(0);
      await loadRawFeed();
    } catch (error) {
      showError(error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async () => {
    if (!onSave) {
      return;
    }
    setSaving(true);
    try {
      await onSave();
    } catch (error) {
      showError(error.message);
    } finally {
      setSaving(false);
    }
  };

  const runNormalize = async (autoMap = false) => {
    const normalizeAction = () => normalizeCatalogFeed(vendorId, sourceTypeConfig, 20, { autoMap });
    const preview = runTrackedAction
      ? await runTrackedAction(normalizeAction, {
        separator: formatActivitySeparator?.(autoMap ? "Normalizzazione + auto-map" : "Normalizzazione", vendorId),
        progressLabel: autoMap ? "Normalizzazione e auto-mapping in corso" : "Normalizzazione in corso",
      })
      : await normalizeAction();
    if (onNormalized) {
      onNormalized(preview, preview.feed_config);
    }
  };

  const handleNormalize = async () => {
    setNormalizing(true);
    try {
      await runNormalize(false);
    } catch (error) {
      showError(error.message);
    } finally {
      setNormalizing(false);
    }
  };

  const handleNormalizeAutoMap = async () => {
    setAutoMapping(true);
    try {
      await runNormalize(true);
    } catch (error) {
      showError(error.message);
    } finally {
      setAutoMapping(false);
    }
  };

  const handleGoToLine = (lineNumber) => {
    const total = rawFeed?.total_lines || 0;
    if (lineNumber < 1 || lineNumber > total) {
      showError(`Riga non valida: inserire un numero tra 1 e ${total}.`);
      return;
    }
    const targetPage = Math.floor((lineNumber - 1) / rowsPerPage);
    setScrollTargetLine(lineNumber);
    if (targetPage !== page) {
      setPage(targetPage);
    }
  };

  return (
    <Paper className="cm-panel">
      <div className="cm-tab-toolbar">
        <div className="cm-tab-toolbar-body cm-feed-config">
          <CatalogNativeSelect
            className="cm-feed-format-select"
            value={feedType}
            title={CM_TOOLTIPS.feedFormat}
            onChange={(nextType) => onSourceTypeConfigChange(
              updateSourceTypeConfig(sourceTypeConfig, { type: nextType }),
            )}
            options={SOURCE_TYPE_OPTIONS}
            ariaLabel="Formato feed"
            disabled={mappingReadOnly}
          />
          <CatalogNativeSelect
            className="cm-feed-header-select"
            value={headerModeValue(sourceTypeConfig)}
            title={CM_TOOLTIPS.feedHeader}
            onChange={(nextValue) => onSourceTypeConfigChange(
              updateSourceTypeConfig(sourceTypeConfig, headerModePatch(nextValue)),
            )}
            options={HEADER_OPTIONS}
            ariaLabel="Presenza intestazione feed"
            disabled={mappingReadOnly}
          />
          <CatalogNativeSelect
            className="cm-feed-gdrive-select"
            value={gdriveModeValue(sourceTypeConfig)}
            title={CM_TOOLTIPS.feedGdrive}
            onChange={(nextValue) => onSourceTypeConfigChange(
              updateSourceTypeConfig(sourceTypeConfig, gdriveModePatch(nextValue)),
            )}
            options={GDRIVE_OPTIONS}
            ariaLabel="Download Google Drive"
            disabled={mappingReadOnly}
          />
        </div>
        <GoToLinePanel
          totalLines={rawFeed?.total_lines || 0}
          onGoToLine={handleGoToLine}
          disabled={loading || !rawFeed?.is_text}
        />
        <div className="cm-tab-toolbar-actions">
        <Button
            variant="outlined"
            onClick={handleOpenFeedUrl}
            disabled={!canOpenFeedUrl}
            title={CM_TOOLTIPS.apriUrlFeed}
          >
            Apri url
          </Button>
          <Button variant="outlined" onClick={handleDownload} disabled={downloading || uploading} title={CM_TOOLTIPS.scaricaFeedRaw}>
            {downloading ? "Download..." : "Scarica feed"}
          </Button>
          <Button
            variant="outlined"
            onClick={handleUploadClick}
            disabled={mappingReadOnly || downloading || uploading}
            title={CM_TOOLTIPS.caricaFeedRaw}
          >
            {uploading ? "Caricamento..." : "Carica feed"}
          </Button>
          <input
            ref={uploadInputRef}
            type="file"
            hidden
            accept={RAW_FEED_UPLOAD_ACCEPT}
            onChange={handleUploadFileChange}
          />
          <Button variant="outlined" onClick={handleSave} disabled={saving || !onSave || mappingReadOnly} title={CM_TOOLTIPS.salvaFeedConfig}>
            {saving ? "Salvataggio..." : "Salva"}
          </Button>
          <Button variant="outlined" onClick={handleNormalize} disabled={!rawFeed?.exists || normalizing || autoMapping || mappingReadOnly} title={CM_TOOLTIPS.normalizza}>
            {normalizing ? "Normalizzazione..." : "Normalizza"}
          </Button>
          <Button variant="contained" onClick={handleNormalizeAutoMap} disabled={!rawFeed?.exists || normalizing || autoMapping || mappingReadOnly} title={CM_TOOLTIPS.normalizzaAutoMap}>
            {autoMapping ? "Auto-map..." : "Normalizza e auto-map"}
          </Button>
        </div>
      </div>

      <CatalogTabStatusMessage message={tabStatus?.text} severity={tabStatus?.severity} />

      {loading && <CatalogLoadingPlaceholder className="cm-spacer-md" />}

      {!loading && rawFeed?.is_text && (
        <>
          <div className="cm-raw-block" ref={rawBlockRef}>
            {(rawFeed.lines || []).map((line, index) => {
              const lineNumber = page * rowsPerPage + index + 1;
              return (
              <div
                key={`raw-line-${lineNumber}`}
                className="cm-raw-line"
                data-feed-line={lineNumber}
              >
                <span className="cm-raw-line-no">{lineNumber}</span>
                <span>{line || " "}</span>
              </div>
              );
            })}
            {(rawFeed.lines || []).length === 0 && (
              <Typography className="cm-muted-text" align="center" sx={{ py: 2 }}>
                File vuoto.
              </Typography>
            )}
          </div>
          <CatalogTablePagination
            rowsPerPage={rowsPerPage}
            setRowsPerPage={setRowsPerPage}
            page={page}
            setPage={setPage}
            totalPages={totalPages}
            countLabel="Righe raw"
            countValue={rawFeed.total_lines || 0}
            rowsOptions={[50, 100, 200, 500]}
          />
        </>
      )}
    </Paper>
  );
};

RawFeedTab.propTypes = {
  vendorId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  feedUrl: PropTypes.string,
  sourceTypeConfig: PropTypes.shape({
    type: PropTypes.string,
    is_headless: PropTypes.bool,
    detection: PropTypes.string,
    auth: PropTypes.bool,
    auth_method: PropTypes.string,
    gdrive: PropTypes.bool,
  }).isRequired,
  onSourceTypeConfigChange: PropTypes.func.isRequired,
  onNormalized: PropTypes.func,
  onSave: PropTypes.func,
  showError: PropTypes.func.isRequired,
  isActive: PropTypes.bool,
  mappingReadOnly: PropTypes.bool,
  runTrackedAction: PropTypes.func,
  formatActivitySeparator: PropTypes.func,
};

export default RawFeedTab;
